import React, { useRef, useState, useEffect } from "react";
import {
  SunIcon,
  RewindIcon,
  SkipIcon,
  PlayIcon,
  PauseIcon,
  VolumeHighIcon,
  CloseIcon,
} from "../components";

const AudioPlayer = ({
  src,
  title,
  excerpt,
  onClose,
  onProgressUpdate,
  isPlaying,
  setIsPlaying,
}) => {
  const audioRef = useRef(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  // const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1); // Volume state
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false); // Mute state
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isMenuHidden, setIsMenuHidden] = useState(false); // Tracks mobile menu visibility
  const [progress, setProgress] = useState(0);

  // Automatically start playing the audio when the component renders
  // useEffect(() => {
  //     if (audioRef.current) {
  //         audioRef.current.play().then(() => {
  //             setIsPlaying(true);
  //         }).catch((error) => {
  //             console.error("Playback failed:", error);
  //         });
  //     }
  // }, [src]); // Re-run when the source changes

  useEffect(() => {
    if (audioRef.current) {
      isPlaying ? audioRef.current.play() : audioRef.current.pause();
    }
  }, [isPlaying, src]);

  useEffect(() => {
    if (showMobileMenu) {
      document.body.classList.add("audio-popup-bg-overlay");
    }

    return () => {
      document.body.classList.remove("audio-popup-bg-overlay");
    };
  }, [showMobileMenu]);

  const handleShowMenu = () => {
    setShowMobileMenu((prevState) => !prevState); // Show the AudioPlayer when clicked
    setIsMenuHidden((prevState) => !prevState); // Toggle mobile menu visibility
  };

  useEffect(() => {
    if (audioRef.current) {
      const audioElement = audioRef.current;
      const updateProgress = () => {
        setCurrentTime(audioElement.currentTime);
        const progressPercentage =
          (audioElement.currentTime / audioElement.duration) * 100;
        onProgressUpdate(progressPercentage); // Update the progress in the parent component
      };
      audioElement.addEventListener("timeupdate", updateProgress);
      audioElement.addEventListener("loadedmetadata", () => {
        setDuration(audioElement.duration);
      });
      return () => {
        audioElement.removeEventListener("timeupdate", updateProgress);
      };
    }
  }, [audioRef, onProgressUpdate]);

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime += 10;
    }
  };

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime -= 10;
    }
  };

  const handlePlaybackRateChange = (event) => {
    const newRate = parseFloat(event.target.value);
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
    setPlaybackRate(newRate);
  };

  const togglePlay = () => {
    if (!src || typeof src !== "string" || src.trim() === "") {
      console.warn("AudioPlayer: Invalid or missing src", src);
    }
    if (!audioRef.current) {
      console.warn("togglePlay: audioRef is missing");
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch((err) => {
        console.error("togglePlay: Audio playback failed", err);
      });
    }
    setIsPlaying((prev) => !prev);
  };

  const handleProgressBarChange = (event) => {
    const newTime = parseFloat(event.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      const newMuteState = !muted;
      audioRef.current.muted = newMuteState;
      setMuted(newMuteState);
      // Set volume to 0 when muted, otherwise restore to previous volume state
      if (newMuteState) {
        setVolume(0);
      } else {
        setVolume(1); // Reset to full volume
      }
    }
  };

  const handleClose = () => {
    onClose(); // Call onClose from parent
  };

  return (
    <>
      {/* Audio desktop menu */}
      <div className="justify-between hidden gap-3 px-6 py-4 border-t border-gray-100 md:flex xl:px-12 fixed  bottom-0 left-0 right-0 z-[99] bg-white-50">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gold-50 w-max">
            <SunIcon className="w-10 h-10 text-gold-200" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            <p className="text-xs text-gray-500">{excerpt}</p>
          </div>
        </div>
        <div className="w-full	xl:max-w-[384px] lg:max-w-[250px] max-w-[200px]">
          {src && src?.endsWith(".mp3") ? (
            <audio ref={audioRef} src={src} preload="metadata" />
          ) : (
            <p className="text-red-500 p-2 text-sm">
              Audio file is missing or unsupported.
            </p>
          )}

          <div>
            <input
              type="range"
              min="0"
              max={duration}
              step="0.1"
              value={currentTime}
              onChange={handleProgressBarChange}
              className="w-full h-2 bg-gray-100 rounded-full appearance-none"
              style={{
                background: `linear-gradient(to right, #A1A1A1 ${(currentTime / duration) * 100}%, #e5e7eb ${(currentTime / duration) * 100}%)`,
              }}
            />
            <div className="flex justify-between">
              <span className="text-gray-600">{formatTime(currentTime)}</span>
              <span className="text-gray-600">{formatTime(duration)}</span>
            </div>
          </div>

          <div className="flex justify-center gap-10">
            <button onClick={skipBackward}>
              <RewindIcon className="text-gray-600" />
            </button>
            <button onClick={togglePlay}>
              {isPlaying ? <PauseIcon /> : <PlayIcon className="w-7 h-7" />}
            </button>
            <button onClick={skipForward}>
              <SkipIcon className="text-gray-600" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div>
            <select
              id="playbackRate"
              className="flex items-center justify-center text-center rounded-full appearance-none bg-gold-50 md:h-11 md:w-11 h-9 w-9 focus:outline-0"
              value={playbackRate}
              onChange={handlePlaybackRateChange}
            >
              <option value="0.5">0.5x</option>
              <option value="0.75">0.75x</option>
              <option value="1">1x</option>
              <option value="1.25">1.25x</option>
              <option value="1.5">1.5x</option>
              <option value="2">2x</option>
            </select>
          </div>
          <button
            className="flex items-center justify-center rounded-full bg-gold-50 md:h-11 md:w-11 h-9 w-9"
            onClick={toggleMute}
          >
            {muted ? <PlayIcon className="w-7 h-7" /> : <VolumeHighIcon />}
          </button>
        </div>
      </div>

      {/* Audio mobile popup */}
      {showMobileMenu && (
        <div className="">
          <div
            className={`md:hidden block bg-audio-popup-gradient px-6 rounded-t-xl transition-transform duration-500 ease-out transform ${showMobileMenu ? "animate-slideUp" : "animate-slideDown"}`}
          >
            <div className="flex justify-center pt-2">
              <button
                onClick={handleShowMenu}
                className="h-1 text-gray-300 border-4 border-t rounded w-9"
              ></button>
            </div>
            <div className="flex justify-end gap-3 pt-3">
              <div>
                <select
                  id="playbackRate"
                  className="flex items-center justify-center text-center rounded-full appearance-none bg-gold-50 md:h-11 md:w-11 h-9 w-9 focus:outline-0"
                  value={playbackRate}
                  onChange={handlePlaybackRateChange}
                >
                  <option value="0.5">0.5x</option>
                  <option value="0.75">0.75x</option>
                  <option value="1">1x</option>
                  <option value="1.25">1.25x</option>
                  <option value="1.5">1.5x</option>
                  <option value="2">2x</option>
                </select>
              </div>
              <div>
                <button
                  className="flex items-center justify-center rounded-full bg-gold-50 md:h-11 md:w-11 h-9 w-9"
                  onClick={toggleMute}
                >
                  {muted ? (
                    <PlayIcon className="w-7 h-7" />
                  ) : (
                    <VolumeHighIcon />
                  )}
                </button>
              </div>
            </div>
            <div className="my-16 mx-auto w-full max-w-[270px]">
              <div className="rounded-2xl bg-white-50 p-70">
                <SunIcon className="text-gold-200 w-full max-w-[124px] h-full max-h-[124px]" />
              </div>
            </div>
            <div>
              <div className="pb-7">
                {" "}
                <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
              </div>
              <div>
                <input
                  type="range"
                  min="0"
                  max={duration}
                  step="0.1"
                  value={currentTime}
                  onChange={handleProgressBarChange}
                  className="w-full h-2 bg-gray-100 rounded-full appearance-none"
                  style={{
                    background: `linear-gradient(to right, #A1A1A1 ${(currentTime / duration) * 100}%, #e5e7eb ${(currentTime / duration) * 100}%)`,
                  }}
                />
                <div className="flex justify-between">
                  <span className="text-xs text-gray-600">
                    {formatTime(currentTime)}
                  </span>
                  <span className="text-xs text-gray-600">
                    {formatTime(duration)}
                  </span>
                </div>
              </div>
              <div className="flex justify-center gap-16 py-12">
                <button onClick={skipBackward}>
                  <RewindIcon className="text-gray-600 w-9 h-9" />
                </button>
                <button onClick={togglePlay}>
                  {isPlaying ? (
                    <PauseIcon className="w-12 h-12" />
                  ) : (
                    <PlayIcon className="w-12 h-12" />
                  )}
                </button>
                <button onClick={skipForward}>
                  <SkipIcon className="text-gray-600 w-9 h-9" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audio mobile menu */}
      {!isMenuHidden && (
        <div className="flex items-center gap-4 p-4 md:hidden bg-white-0 shadow-custom-gray">
          <div
            onClick={handleShowMenu}
            className="flex items-center w-3/4 gap-3"
          >
            <div className="p-2 rounded-lg bg-gold-50 w-max">
              <SunIcon className="w-10 h-10 text-gold-200" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">{title}</h2>
              <p className="text-xs text-gray-500">{excerpt}</p>
            </div>
          </div>
          <div className="flex items-center justify-end w-1/4 gap-4">
            <div className="flex items-center">
              <button onClick={togglePlay}>
                {isPlaying ? <PauseIcon /> : <PlayIcon className="w-7 h-7" />}
              </button>
            </div>

            <div className="flex items-center">
              <button onClick={handleClose}>
                <CloseIcon className="text-gray-500" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Helper function to format time in MM:SS format
const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
};

export default AudioPlayer;
